import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/lib/i18n";

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clear } = useCart();
  const { t } = useLang();
  const [state, setState] = useState("verwerken"); // verwerken | betaald | timeout
  const [orderId, setOrderId] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const d = await api.get(`/payments/status/${sessionId}`);
        setOrderId(d.data.order_id);
        if (d.data.payment_status === "paid") {
          clear();
          setState("betaald");
          return;
        }
      } catch {
        /* volgende poging */
      }
      if (attempts.current >= 12) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 pt-24">
      <div className="max-w-lg w-full text-center border border-gold/20 bg-elevate p-12" data-testid="payment-success-card">
        {state === "betaald" ? (
          <>
            <CheckCircle2 size={44} className="text-gold mx-auto mb-6" />
            <h1 className="font-serif font-light text-cream text-3xl">{t("success.done")}</h1>
            <p className="mt-4 text-sm text-mist leading-relaxed">
              {t("success.doneSub")}
            </p>
            {orderId && (
              <p className="mt-6 font-mono text-xs tracking-[0.25em] uppercase text-gold" data-testid="order-number">
                {t("success.orderNr")}: {orderId}
              </p>
            )}
            <Link data-testid="success-to-account" to="/account/bestellingen" className="inline-block mt-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-4 hover:bg-gold-light transition-colors">
              {t("success.view")}
            </Link>
          </>
        ) : state === "timeout" ? (
          <>
            <h1 className="font-serif font-light text-cream text-3xl">{t("success.timeout")}</h1>
            <p className="mt-4 text-sm text-mist leading-relaxed">
              {t("success.timeoutSub")}
            </p>
            <Link data-testid="success-to-account" to="/account/bestellingen" className="inline-block mt-8 border border-gold/40 text-gold font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-4 hover:bg-gold/10 transition-colors">
              {t("success.toOrders")}
            </Link>
          </>
        ) : (
          <>
            <Loader2 size={40} className="text-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-serif font-light text-cream text-3xl">{t("success.processing")}</h1>
            <p className="mt-4 text-sm text-mist">{t("success.processingSub")}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
